import React, { useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';
import { Link } from 'expo-router';

import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { WORK_COMPOSER_PLACEHOLDER } from '@/constants/work';

import { useInteractive, useLanding } from './LandingKit';
import { EASE, isWeb, prefersReducedMotion } from './landingTokens';

/**
 * Every click inside the composer lands here. The page is only ever shown to
 * signed-out visitors, so there is nothing to prefill — the account comes first.
 */
const COMPOSER_HREF = '/signup';

/**
 * Goals the empty composer cycles through after the real placeholder has had
 * its moment. Each one is something Work actually ships (see SCENARIOS in
 * HeroWorkShowcase and the agent-skills directory).
 */
const EXAMPLE_GOALS = [
  'Make a cinematic product video with music and a voiceover…',
  'Build a playable arcade game with a score and restart button…',
  'Research our competitors and build a comparison deck…',
  'Analyze this spreadsheet and turn the findings into a report…',
  'Code a website for my business and give me the source files…',
  'Turn my notes into a proposal, a slide deck, and a budget…',
];

const PLACEHOLDER_HOLD_MS = 4200;
const GOAL_TYPE_MS = 22;
const GOAL_HOLD_MS = 2600;
const GOAL_ERASE_MS = 8;

/* ------------------------------------------------------------------ */
/* Composer                                                            */
/* ------------------------------------------------------------------ */

/**
 * Cycles the empty composer's text: the real placeholder first, then example
 * goals typed and erased one after another. Returns the string to show and
 * whether it is currently "being typed" (so the caret can show).
 */
function usePlaceholderCycle(enabled) {
  const [state, setState] = useState({ text: WORK_COMPOSER_PLACEHOLDER, typing: false });

  useEffect(() => {
    if (!enabled) return undefined;
    let timer = 0;
    const schedule = (fn, ms) => { timer = setTimeout(fn, ms); };

    const run = (goalIndex, typed, phase) => {
      const goal = EXAMPLE_GOALS[goalIndex];
      if (phase === 'type') {
        if (typed < goal.length) {
          setState({ text: goal.slice(0, typed + 1), typing: true });
          schedule(() => run(goalIndex, typed + 1, 'type'), GOAL_TYPE_MS);
        } else {
          setState({ text: goal, typing: true });
          schedule(() => run(goalIndex, typed, 'erase'), GOAL_HOLD_MS);
        }
        return;
      }
      if (typed > 0) {
        setState({ text: goal.slice(0, typed - 1), typing: true });
        schedule(() => run(goalIndex, typed - 1, 'erase'), GOAL_ERASE_MS);
      } else {
        const next = (goalIndex + 1) % EXAMPLE_GOALS.length;
        if (next === 0) {
          setState({ text: WORK_COMPOSER_PLACEHOLDER, typing: false });
          schedule(() => run(0, 0, 'type'), PLACEHOLDER_HOLD_MS);
        } else {
          schedule(() => run(next, 0, 'type'), 320);
        }
      }
    };

    schedule(() => run(0, 0, 'type'), PLACEHOLDER_HOLD_MS);
    return () => clearTimeout(timer);
  }, [enabled]);

  return state;
}

/** A control inside the composer. Looks like the real thing; navigates to signup. */
function ComposerControl({ children, label, style, hoverStyle }) {
  const { c } = useLanding();
  const { hovered, handlers } = useInteractive();
  const body = (
    <Pressable
      {...handlers}
      accessibilityRole="link"
      accessibilityLabel={label}
      style={{
        alignItems: 'center',
        justifyContent: 'center',
        ...(isWeb ? { cursor: 'pointer', transition: `background-color 160ms ${EASE}` } : {}),
        ...style,
        ...(hovered ? (hoverStyle || { backgroundColor: c.panel }) : {}),
      }}
    >
      {children}
    </Pressable>
  );
  if (!isWeb) return body;
  return <Link href={COMPOSER_HREF} asChild>{body}</Link>;
}

/**
 * A pixel-faithful stand-in for the Work composer (ChatInputFloating in Work
 * mode): attach button, the empty text area, the thinking-power pill and the
 * dictate mic. Nothing here is interactive except that it all links to signup.
 */
function MockComposer({ hovered, handlers }) {
  const { c, isDark, isXS } = useLanding();
  const cycle = usePlaceholderCycle(isWeb && !prefersReducedMotion());
  const showingPlaceholder = !cycle.typing && cycle.text === WORK_COMPOSER_PLACEHOLDER;

  const surface = isDark ? '#111412' : '#FFFFFF';
  const borderColor = hovered ? c.accent : c.ruleStrong;

  const shell = (
    <Pressable
      {...handlers}
      accessibilityRole="link"
      accessibilityLabel="Unlock Gab AI Work Agent with Plus"
      style={{
        width: '100%',
        borderRadius: 18,
        borderWidth: 1,
        borderColor,
        backgroundColor: surface,
        overflow: 'hidden',
        ...(isWeb ? {
          cursor: 'text',
          boxShadow: hovered
            ? `0 0 0 3px ${c.accentWash}, ${c.shadowSoft}`
            : c.shadowSoft,
          transition: `border-color 220ms ${EASE}, box-shadow 220ms ${EASE}`,
        } : {}),
      }}
    >
      {/* Text row */}
      <View style={{ minHeight: 78, paddingLeft: 22, paddingRight: 22, paddingVertical: 20, flexDirection: 'row', alignItems: 'center' }}>
        <Text
          numberOfLines={1}
          style={{
            flex: 1,
            minWidth: 0,
            color: showingPlaceholder ? c.muted : c.ink,
            fontSize: 17,
            lineHeight: 24,
            letterSpacing: -0.1,
          }}
        >
          {cycle.text}
          {isWeb && cycle.typing ? (
            <Text dataSet={{ landingCaret: 'true' }} style={{ color: c.accent, fontSize: 17, lineHeight: 24 }}>|</Text>
          ) : null}
        </Text>
      </View>

      {/* Lower row: + on the left, Fast ▾ and mic on the right */}
      <View
        style={{
          height: isXS ? 46 : 40,
          paddingHorizontal: 6,
          paddingBottom: 6,
          paddingTop: isXS ? 6 : 0,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <ComposerControl
          label="Sign up to attach files and use tools"
          style={{ height: 34, minWidth: 34, borderRadius: 17, paddingHorizontal: 8 }}
        >
          <Icon name="plus" size={18} color={c.ink} />
        </ComposerControl>

        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 1 }}>
          <ComposerControl
            label="Sign up to choose thinking power"
            style={{ height: 34, borderRadius: 18, paddingLeft: 10, paddingRight: 6, flexDirection: 'row', gap: 6 }}
          >
            <Icon name="bolt" size={12} color={c.inkSoft} solid />
            {!isXS ? <Text style={{ color: c.ink, fontSize: 14 }}>Fast</Text> : null}
            <Icon name="chevron-down" size={8} color={c.faint} />
          </ComposerControl>

          <ComposerControl
            label="Sign up to dictate a goal"
            style={{ width: 38, height: 38, borderRadius: 19 }}
            hoverStyle={{}}
          >
            <View
              style={{
                width: 32,
                height: 32,
                borderRadius: 16,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isDark ? 'rgba(243,246,243,0.08)' : 'rgba(15,21,18,0.06)',
                ...(isWeb ? { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' } : {}),
              }}
            >
              <Icon name="microphone-lines" size={14} color={c.muted} solid />
            </View>
          </ComposerControl>
        </View>
      </View>
    </Pressable>
  );

  if (!isWeb) return shell;
  return <Link href={COMPOSER_HREF} asChild>{shell}</Link>;
}

/* ------------------------------------------------------------------ */
/* Section                                                             */
/* ------------------------------------------------------------------ */

/**
 * The Work composer, lifted straight out of the product and into the hero.
 * Signed-in users see the real one at /app; this is the same control, one
 * click from an account.
 */
function HeroWorkHome({ entered = true }) {
  const { hovered: composerHovered, handlers: composerHandlers } = useInteractive();

  return (
    <View
      style={{
        width: '100%',
        maxWidth: 780,
        alignSelf: 'center',
        alignItems: 'center',
        ...(isWeb ? {
          opacity: entered ? 1 : 0,
          transform: [{ translateY: entered ? 0 : 24 }],
          transition: `opacity 900ms ${EASE} 260ms, transform 900ms ${EASE} 260ms`,
        } : {}),
      }}
    >
      <MockComposer hovered={composerHovered} handlers={composerHandlers} />

    </View>
  );
}

export default React.memo(HeroWorkHome);
